import * as moment from "moment";

import { AnsweredQuestionWithId, DropdownOption, FileAnswer, QuestionAnswer } from "../questionnaires/model";

export const DATE_FORMAT = "YYYY-MM-DD";

export const INITIAL_WORK_ADJUSTMENT_QUESTIONNAIRE_KEY = "INITIAL_WORK_ADJUSTMENT_QUESTIONNAIRE_KEY";
export const UPDATE_WORK_ADJUSTMENT_QUESTIONNAIRE_KEY = "UPDATE_WORK_ADJUSTMENT_QUESTIONNAIRE_KEY";

interface QuestionnaireModel {
    questions: AnsweredQuestionWithId[];
    eTag: string;
}

export interface InitialWorkAdjustmentQuestionnaireModel extends QuestionnaireModel {}
export interface UpdateWorkAdjustmentQuestionnaireModel extends QuestionnaireModel {}

export interface ParentReferralReportModel {
    readonly hrManager: string;
    readonly employeeSeen: string;
    readonly dateOfBirth: moment.Moment;
    readonly employeeUniqueId: string;
    readonly employerAndDivision: string;
    readonly jobTitle: string;
    readonly dateOfReport: moment.Moment;
    readonly recommendedAdjustments: string[];
    readonly recommendedEndDate: moment.Moment;
    readonly advisedTargetReturnToWorkDate: moment.Moment;
    readonly workClassification: string;
}

export const InitialWorkAdjustmentQuestionnaireSections = {
    InformationFromOhReport: "InformationFromOhReport",
    MeetingRecord: "MeetingRecord",
    ClosureOfAdjustments: "ClosureOfAdjustments"
};

export const InitialWorkAdjustmentQuestionnaireSectionTitles = {
    InformationFromOhReport: "Information from OH Report",
    MeetingRecord: "Meeting Record",
    ClosureOfAdjustments: "Closure of Adjustments"
};

export const UpdateWorkAdjustmentQuestionnaireSections = {
    InformationFromOhReport: "InformationFromOhReport",
    ReviewOfAdjustments: "ReviewOfAdjustments",
    NewAdjustmentDetails: "NewAdjustmentDetails",
    ClosureOfAdjustments: "ClosureOfAdjustments",
    ReReferral: "ReReferral"
};

export const UpdateWorkAdjustmentQuestionnaireSectionTitles = {
    InformationFromOhReport: "Information from OH Report",
    ReviewOfAdjustments: "Review of Adjustments",
    NewAdjustmentDetails: "New Adjustment Details",
    ClosureOfAdjustments: "Closure of Adjustments",
    ReReferral: "Re-referral"
};

export enum WorkAdjustmentTypes {
    LessThanSixWeeksShortTerm = "LessThan6WeeksShortTerm",
    SixWeeksPlusLongTerm = "SixWeeksPlusLongTerm",
    NoAdjustment = "NoAdjustment"
}

export const workAdjustmentTypeOptions: DropdownOption[] = [
    { text: "Less than 6 weeks (short term)", value: WorkAdjustmentTypes.LessThanSixWeeksShortTerm },
    { text: "6 weeks+ (long term)", value: WorkAdjustmentTypes.SixWeeksPlusLongTerm },
    { text: "No adjustment", value: WorkAdjustmentTypes.NoAdjustment }
];

export enum ShiftTypes {
    Days = "Days",
    Nights = "Nights"
}

export const shiftTypesOptions: DropdownOption[] = [
    { text: "Days", value: ShiftTypes.Days },
    { text: "Nights", value: ShiftTypes.Nights }
];

export const daysPlannedForWorkOptions = {
    Monday: "Monday",
    Tuesday: "Tuesday",
    Wednesday: "Wednesday",
    Thursday: "Thursday",
    Friday: "Friday",
    Saturday: "Saturday",
    Sunday: "Sunday"
};

export enum ReasonForClosure {
    LeftEmployment = "LeftEmployment",
    ReturnToWorkOnFullDuties = "ReturnToWorkOnFullDuties"
}

export const reasonForClosureOptions: DropdownOption[] = [
    { text: "Left Employment", value: ReasonForClosure.LeftEmployment },
    { text: "Return to work on full duties", value: ReasonForClosure.ReturnToWorkOnFullDuties }
];

export enum TypesOfAppointmentRequested {
    BookWorkAdjustmentFollowUpTelephoneOhaReferralTask = "BookWorkAdjustmentFollowUpTelephoneOhaReferralTask",
    BookWorkAdjustmentFollowUpTelephoneOhpReferralTask = "BookWorkAdjustmentFollowUpTelephoneOhpReferralTask",
    BookWorkAdjustmentFollowUpTelephonePhysioReferralTask = "BookWorkAdjustmentFollowUpTelephonePhysioReferralTask",
    BookWorkAdjustmentFollowUpVideoOhaReferralTask = "BookWorkAdjustmentFollowUpVideoOhaReferralTask",
    BookWorkAdjustmentFollowUpVideoOhpReferralTask = "BookWorkAdjustmentFollowUpVideoOhpReferralTask",
    BookWorkAdjustmentFollowUpVideoPhysioReferralTask = "BookWorkAdjustmentFollowUpVideoPhysioReferralTask"
}

export const typeOfAppointmentRequestedOptions: DropdownOption[] = [
    { text: "Book Follow Up OHA Telephone", value: TypesOfAppointmentRequested.BookWorkAdjustmentFollowUpTelephoneOhaReferralTask },
    { text: "Book Follow Up OHP Telephone", value: TypesOfAppointmentRequested.BookWorkAdjustmentFollowUpTelephoneOhpReferralTask },
    { text: "Book Follow Up Physio Telephone", value: TypesOfAppointmentRequested.BookWorkAdjustmentFollowUpTelephonePhysioReferralTask },
    { text: "Book Follow Up OHA Video", value: TypesOfAppointmentRequested.BookWorkAdjustmentFollowUpVideoOhaReferralTask },
    { text: "Book Follow Up OHP Video", value: TypesOfAppointmentRequested.BookWorkAdjustmentFollowUpVideoOhpReferralTask },
    { text: "Book Follow Up Physio Video", value: TypesOfAppointmentRequested.BookWorkAdjustmentFollowUpVideoPhysioReferralTask },
];

export const workAdjustmentFormsAcceptedExtentions = ["pdf", "doc", "docx"];

export const noFilesUploaded = (workAdjustmentQuestionnaire: QuestionnaireModel, uploadQuestionId: string, reasonForClosureQuestionAnswer: QuestionAnswer) => {
    if (reasonForClosureQuestionAnswer !== ReasonForClosure.LeftEmployment) {
        const notesFromMeetingUploadedAnsweredQuestion = workAdjustmentQuestionnaire?.questions?.find(q => q.id === uploadQuestionId);
        const uploadedFiles = notesFromMeetingUploadedAnsweredQuestion?.answer as FileAnswer[] ?? [];
        const noFiles = uploadedFiles?.length === 0 || uploadedFiles?.every(f => !f.blobId);
        return noFiles;
    }
    return false;
};

export const getWorkAdjustmentReviewDate = (endDateOfAdjustmentAnswer: string): string => {
    const endDateOfAdjustmentAnswerValue = moment(endDateOfAdjustmentAnswer, DATE_FORMAT);
    if (endDateOfAdjustmentAnswerValue && endDateOfAdjustmentAnswerValue.isValid()) {
        const newWorkAdjustmentReviewDate = endDateOfAdjustmentAnswerValue.subtract(7, "days").format(DATE_FORMAT);
        return newWorkAdjustmentReviewDate;
    }
    return "";
};

export const isQuestionAnswerValid = (id: string, questionnaire: QuestionnaireModel): boolean => {
    return questionnaire?.questions?.find(question => question.id === id)?.isValid;
};

export const isLessThanSixWeeksIntoTheFuture = (value: moment.Moment): boolean => {
    return value && value.isBefore(moment().add(6, "weeks"));
};

export const isInTheFuture = (value: moment.Moment): boolean => {
    return value && value.isSameOrAfter(moment().startOf("day"));
};

export enum SecondaryCaseStatus {
    Unknown = "Unknown",
    Pending = "Pending",
    Updated = "Updated",
    Viewed = "Viewed"
}

export const secondaryCaseStatusOptions: DropdownOption[] = [
    { text: "Updated", value: SecondaryCaseStatus.Updated },
    { text: "Viewed", value: SecondaryCaseStatus.Viewed },
    { text: "All", value: "All" }
];

export const hrrcUseOnlyCaseStatusOptions: DropdownOption[] = [
    { text: "Updated", value: SecondaryCaseStatus.Updated },
    { text: "Viewed", value: SecondaryCaseStatus.Viewed }
];
